import { useCallback, useEffect, useState } from 'react';
import {
  governanceApi,
  type ActivityEntry,
  type AuditLogEntry,
  type ComplianceControl,
  type GovernanceDashboard,
  type GovernancePolicy,
  type PrivacyRequest,
  type RetentionRule,
  type RiskAssessment,
  type TrustReport,
} from '../../api/governance';

type Tab = 'overview' | 'policies' | 'compliance' | 'privacy' | 'audit' | 'risk' | 'security' | 'trust';

export function GovernancePlatformPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<GovernanceDashboard | null>(null);
  const [policies, setPolicies] = useState<GovernancePolicy[]>([]);
  const [audits, setAudits] = useState<AuditLogEntry[]>([]);
  const [controls, setControls] = useState<ComplianceControl[]>([]);
  const [privacy, setPrivacy] = useState<PrivacyRequest[]>([]);
  const [risks, setRisks] = useState<RiskAssessment[]>([]);
  const [retention, setRetention] = useState<RetentionRule[]>([]);
  const [trust, setTrust] = useState<TrustReport | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [auditSearch, setAuditSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, pol, aud, ctrl, priv, rsk, ret, tr, act] = await Promise.all([
        governanceApi.dashboard(),
        governanceApi.listPolicies(),
        governanceApi.listAudits(),
        governanceApi.listCompliance(),
        governanceApi.listPrivacyRequests(),
        governanceApi.listRisks(),
        governanceApi.listRetentionRules(),
        governanceApi.trustCenter(),
        governanceApi.listActivity(),
      ]);
      setDashboard(dash);
      setPolicies(pol);
      setAudits(aud);
      setControls(ctrl);
      setPrivacy(priv);
      setRisks(rsk);
      setRetention(ret);
      setTrust(tr);
      setActivity(act);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load governance platform');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const evaluateAI = async () => {
    setBusy(true);
    try {
      await governanceApi.evaluatePolicy({ category: 'ai_execution', action: 'execute' });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const submitPrivacy = async () => {
    setBusy(true);
    try {
      await governanceApi.submitPrivacyRequest({ type: 'access', detail: 'Data subject access request' });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const searchAudits = async () => {
    setBusy(true);
    try {
      const results = await governanceApi.searchAudits(auditSearch || 'login');
      setAudits(results);
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'policies', label: 'Policy Manager' },
    { id: 'compliance', label: 'Compliance Center' },
    { id: 'privacy', label: 'Privacy Center' },
    { id: 'audit', label: 'Audit Explorer' },
    { id: 'risk', label: 'Risk Center' },
    { id: 'security', label: 'Security' },
    { id: 'trust', label: 'Trust Center' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Governance &amp; Trust Platform</h1>
          <p className="text-sm text-slate-500">
            Policies, compliance, privacy, audit, risk &amp; trust — FCP-00014
          </p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-indigo-100 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Active Policies', value: dashboard.active_policies },
            { label: 'Audit Events (24h)', value: dashboard.audit_events_24h },
            { label: 'Open Risks', value: dashboard.open_risks },
            { label: 'Trust Score', value: `${(dashboard.trust_score * 100).toFixed(0)}%` },
            { label: 'Policy Violations', value: dashboard.policy_violations_24h },
            { label: 'Security Events', value: dashboard.security_events_24h },
            { label: 'Privacy Requests', value: dashboard.pending_privacy_requests },
            { label: 'Compliance Controls', value: dashboard.compliance_controls },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'policies' && (
        <div className="space-y-4">
          <button type="button" onClick={evaluateAI} disabled={busy}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
            Test AI Policy Evaluation
          </button>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Version</th></tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium">{p.name}</td>
                    <td className="px-4 py-3">{p.category}</td>
                    <td className="px-4 py-3">{p.status}</td>
                    <td className="px-4 py-3">v{p.version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'compliance' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {controls.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-900">{c.name}</span>
                <span className={`rounded px-2 py-0.5 text-xs ${c.status === 'implemented' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                  {c.status}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500">{c.framework.toUpperCase()} · {c.control_id}</p>
              <p className="mt-2 text-sm text-slate-600">{c.description}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'privacy' && (
        <div className="space-y-4">
          <button type="button" onClick={submitPrivacy} disabled={busy}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
            Submit Access Request
          </button>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr><th className="px-4 py-3">Type</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Detail</th><th className="px-4 py-3">Created</th></tr>
              </thead>
              <tbody>
                {privacy.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="px-4 py-3">{p.type}</td>
                    <td className="px-4 py-3">{p.status}</td>
                    <td className="px-4 py-3">{p.detail || '—'}</td>
                    <td className="px-4 py-3 text-xs">{new Date(p.created_at).toLocaleString()}</td>
                  </tr>
                ))}
                {privacy.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500">No privacy requests</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'audit' && (
        <div className="space-y-4">
          <div className="flex gap-3">
            <input value={auditSearch} onChange={(e) => setAuditSearch(e.target.value)}
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Search audit logs..." />
            <button type="button" onClick={searchAudits} disabled={busy}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">Search</button>
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr><th className="px-4 py-3">Domain</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Detail</th><th className="px-4 py-3">Time</th></tr>
              </thead>
              <tbody>
                {audits.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100">
                    <td className="px-4 py-3">{a.domain}</td>
                    <td className="px-4 py-3">{a.action}</td>
                    <td className="px-4 py-3 max-w-xs truncate">{a.detail}</td>
                    <td className="px-4 py-3 text-xs">{new Date(a.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'risk' && (
        <div className="space-y-4">
          {risks.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{r.title}</span>
                <span className={`rounded px-2 py-0.5 text-xs font-medium ${
                  r.level === 'critical' ? 'bg-red-100 text-red-800' :
                  r.level === 'high' ? 'bg-orange-100 text-orange-800' :
                  r.level === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                }`}>{r.level}</span>
              </div>
              <p className="mt-1 text-sm text-slate-600">{r.description}</p>
              <p className="mt-2 text-xs text-slate-500">Mitigation: {r.mitigation}</p>
            </div>
          ))}
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold">Retention Rules</h3>
            <ul className="mt-2 space-y-1 text-sm text-slate-600">
              {retention.map((r) => (
                <li key={r.id}>{r.name}: {r.retention_days} days ({r.classification})</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'security' && dashboard && (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h3 className="text-lg font-semibold">Security Posture</h3>
          <p className="mt-2 text-sm text-slate-600">
            {dashboard.security_events_24h} security events in the last 24 hours.
            RBAC, ABAC-ready architecture, encryption, and threat detection hooks are active.
          </p>
        </div>
      )}

      {tab === 'trust' && trust && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { label: 'Overall Trust', value: trust.overall_trust_score },
            { label: 'Security', value: trust.security_score },
            { label: 'Compliance', value: trust.compliance_score },
            { label: 'AI Governance', value: trust.ai_governance_score },
            { label: 'Marketplace', value: trust.marketplace_score },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs text-slate-500">{s.label}</p>
              <p className="mt-1 text-2xl font-semibold">{(s.value * 100).toFixed(0)}%</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'overview' && activity.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold">Recent Activity</h3>
          <ul className="mt-2 space-y-2 text-sm text-slate-600">
            {activity.slice(0, 8).map((a) => (
              <li key={a.id}><span className="font-medium">{a.action}</span> — {a.message}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
